(() => {
  class SosReviewCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector(".sos-review-carousel__viewport");
      this.track = root.querySelector("[data-sos-review-track]");
      this.slides = [...root.querySelectorAll("[data-sos-review-slide]")];
      this.dots = [...root.querySelectorAll("[data-sos-review-dot]")];
      this.expandButtons = [...root.querySelectorAll("[data-sos-review-expand]")];
      this.interval = Number(root.dataset.interval) || 7000;
      this.index = 0;
      this.timer = null;
      this.interacting = false;
      this.visible = true;
      this.pointerId = null;
      this.pointerStartX = 0;
      this.pointerCurrentX = 0;
      this.pointerStartTime = 0;
      this.dragging = false;
      this.suppressClick = false;
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

      if (this.slides.length < 2) return;

      this.bind();
      this.show(0);
      this.observe();
      this.syncAutoplay();
    }

    bind() {
      this.dots.forEach((dot) => {
        dot.addEventListener("click", () => {
          const dotIndex = Number(dot.dataset.sosReviewDot);
          const cycleStart = Math.floor(this.index / this.dots.length) * this.dots.length;
          const targetIndex = cycleStart + dotIndex;
          this.show(targetIndex < this.slides.length ? targetIndex : dotIndex);
          this.syncAutoplay();
        });
        dot.addEventListener("pointerdown", () => {
          this.interacting = true;
          this.syncAutoplay();
        });
        const resumeAfterDot = () => {
          this.interacting = false;
          this.syncAutoplay();
        };
        dot.addEventListener("pointerup", resumeAfterDot);
        dot.addEventListener("pointercancel", resumeAfterDot);
      });

      this.expandButtons.forEach((button) => {
        button.addEventListener("click", () => {
          const slide = button.closest("[data-sos-review-slide]");
          const expanded = !slide.classList.contains("is-expanded");
          slide.classList.toggle("is-expanded", expanded);
          button.setAttribute("aria-expanded", String(expanded));
          button.querySelector("[data-sos-review-expand-label]").textContent =
            expanded ? "Ver menos" : "Ver mais";
          this.syncAutoplay();
        });
      });

      this.viewport.addEventListener("pointerdown", (event) => this.onPointerDown(event));
      this.viewport.addEventListener("pointermove", (event) => this.onPointerMove(event));
      this.viewport.addEventListener("pointerup", (event) => this.onPointerEnd(event));
      this.viewport.addEventListener("pointercancel", (event) => this.onPointerEnd(event, true));
      this.viewport.addEventListener(
        "click",
        (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        },
        true,
      );

      this.root.addEventListener("mouseenter", () => {
        if (!window.matchMedia("(hover: hover)").matches) return;
        this.interacting = true;
        this.syncAutoplay();
      });
      this.root.addEventListener("mouseleave", () => {
        if (!window.matchMedia("(hover: hover)").matches) return;
        this.interacting = false;
        this.syncAutoplay();
      });
      this.root.addEventListener("focusin", (event) => {
        if (!event.target.matches(":focus-visible")) return;
        this.interacting = true;
        this.syncAutoplay();
      });
      this.root.addEventListener("focusout", (event) => {
        if (!this.root.contains(event.relatedTarget)) {
          this.interacting = false;
          this.syncAutoplay();
        }
      });

      document.addEventListener("visibilitychange", () => this.syncAutoplay());
      this.reducedMotion.addEventListener?.("change", () => this.syncAutoplay());
      window.addEventListener("resize", () => this.measureQuotes());
      this.measureQuotes();
    }

    onPointerDown(event) {
      if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerCurrentX = event.clientX;
      this.pointerStartTime = performance.now();
      this.dragging = false;
      this.interacting = true;
      this.viewport.setPointerCapture?.(event.pointerId);
      this.syncAutoplay();
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      let distance = event.clientX - this.pointerStartX;
      this.pointerCurrentX = event.clientX;

      if (Math.abs(distance) < 6 && !this.dragging) return;

      this.dragging = true;
      this.root.classList.add("is-dragging");

      const isPastStart = this.index === 0 && distance > 0;
      const isPastEnd = this.index === this.slides.length - 1 && distance < 0;
      if (isPastStart || isPastEnd) distance *= 0.24;

      this.track.style.transform = `translate3d(calc(-${this.index * 100}% + ${distance}px), 0, 0)`;
      event.preventDefault();
    }

    onPointerEnd(event, cancelled = false) {
      if (event.pointerId !== this.pointerId) return;

      this.pointerCurrentX = event.clientX;
      const distance = this.pointerCurrentX - this.pointerStartX;
      const elapsed = Math.max(performance.now() - this.pointerStartTime, 1);
      const velocity = Math.abs(distance) / elapsed;
      const threshold = Math.min(68, Math.max(38, this.viewport.clientWidth * 0.13));
      const shouldChange =
        !cancelled && this.dragging && (Math.abs(distance) >= threshold || velocity > 0.48);

      let nextIndex = this.index;
      if (shouldChange && distance < 0 && this.index < this.slides.length - 1) {
        nextIndex += 1;
      } else if (shouldChange && distance > 0 && this.index > 0) {
        nextIndex -= 1;
      }

      this.suppressClick = this.dragging;
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 0);

      const wasDragging = this.dragging;
      this.root.classList.remove("is-dragging");
      this.pointerId = null;
      this.dragging = false;
      this.interacting = false;
      if (wasDragging) this.track.getBoundingClientRect();
      this.show(nextIndex);
      this.syncAutoplay();
    }

    observe() {
      if (!("IntersectionObserver" in window)) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry.isIntersecting;
          this.syncAutoplay();
        },
        { threshold: 0.25 },
      );
      this.observer.observe(this.root);
    }

    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.index;
        const expand = slide.querySelector("[data-sos-review-expand]");
        expand?.setAttribute("tabindex", isActive ? "0" : "-1");
        if (!isActive) {
          slide.classList.remove("is-expanded");
          expand?.setAttribute("aria-expanded", "false");
          const label = expand?.querySelector("[data-sos-review-expand-label]");
          if (label) label.textContent = "Ver mais";
        }
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", String(!isActive));
      });

      const activeDotIndex = this.index % this.dots.length;

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeDotIndex;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", String(isActive));
      });
    }

    measureQuotes() {
      window.requestAnimationFrame(() => {
        this.slides.forEach((slide) => {
          const quote = slide.querySelector("[data-sos-review-quote]");
          if (!quote) return;
          const isClamped = quote.scrollHeight > quote.clientHeight + 1;
          slide.classList.toggle("is-collapsible", isClamped);
        });
      });
    }

    syncAutoplay() {
      window.clearTimeout(this.timer);
      this.timer = null;

      const shouldRun =
        !this.interacting &&
        !this.root.querySelector(".is-expanded") &&
        !this.reducedMotion.matches &&
        !document.hidden &&
        this.visible;

      this.root.classList.toggle("is-running", shouldRun);

      if (!shouldRun) return;

      this.timer = window.setTimeout(() => {
        this.show(this.index + 1);
        this.syncAutoplay();
      }, this.interval);
    }
  }

  const init = () => {
    document.querySelectorAll("[data-sos-review-carousel]").forEach((root) => {
      if (root.dataset.sosReviewReady === "true") return;
      root.dataset.sosReviewReady = "true";
      new SosReviewCarousel(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
